import type { IconType } from 'react-icons';
import { BiAnchor, BiBriefcase, BiCompass, BiSun, BiWrench } from 'react-icons/bi';
import {
  FiAnchor,
  FiAward,
  FiBriefcase,
  FiCalendar,
  FiClipboard,
  FiCoffee,
  FiCompass,
  FiDollarSign,
  FiFileText,
  FiNavigation,
  FiTool,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';

import type { Choice } from '@/components';
import { CV_AUTOFILL_ENABLED, INVITE_CODES_ENABLED } from '@/utils/featureFlags';
import agentIcon from '@assets/service_provider_categories/agent_services.svg';
import crewIcon from '@assets/service_provider_categories/crew_services.svg';
import electronicsIcon from '@assets/service_provider_categories/electronics_navigation_communication.svg';
import emergencyIcon from '@assets/service_provider_categories/emergency_services.svg';
import washIcon from '@assets/service_provider_categories/exterior_wash_detail.svg';
import hullIcon from '@assets/service_provider_categories/hull_exterior.svg';
import interiorIcon from '@assets/service_provider_categories/interior_cleaning.svg';
import mechanicalIcon from '@assets/service_provider_categories/mechanical_electrical.svg';
import conciergeIcon from '@assets/service_provider_categories/operational_concierge.svg';
import safetyIcon from '@assets/service_provider_categories/safety_inspection.svg';
import upholsteryIcon from '@assets/service_provider_categories/upholstery_canvas.svg';
import yachtmgmtIcon from '@assets/service_provider_categories/yacht_management.svg';

export type IntentId = 'vessel' | 'services' | 'guest' | 'job';
export type Role = 'owner' | 'captain';
export type JobPath = 'job' | 'team';
export type PlanId = 'harbor' | 'navigate' | 'command';

export type StepId =
  | 'intent'
  | 'role'
  | 'vessel'
  | 'needs'
  | 'crewInfo'
  | 'serviceProfile'
  | 'serviceTypeChips'
  | 'marinaInfo'
  | 'serviceContact'
  | 'location'
  | 'guestAllergies'
  | 'guestDiet'
  | 'vesselCode'
  | 'invite'
  | 'goal'
  | 'crewProfile'
  | 'joinVessel'
  | 'review'
  | 'subscription';

// Every step except the opening intent screen; what the flow arrays hold.
export type FlowStep = Exclude<StepId, 'intent'>;

/** The cards moved to the design system; the flow's lists keep naming their type here. */
export type { Choice };

export interface FeatureOption {
  id: string;
  label: string;
  Icon: IconType;
}

interface SelectOption {
  label: string;
  value: string;
}

/** Subscription tier card. Harbor is free; Navigate/Command are per-seat/month. */
interface Plan {
  id: PlanId;
  name: string;
  Icon: IconType;
  tagline: string;
  free?: boolean;
  monthly?: number;
}

export const INTENTS: Choice<IntentId>[] = [
  {
    id: 'vessel',
    title: 'I own or run a vessel',
    desc: 'Set up your vessel and manage crew, maintenance and money in one place.',
    Icon: BiAnchor,
  },
  {
    id: 'services',
    title: 'I provide services',
    desc: 'Show owners and captains what you offer.',
    Icon: BiWrench,
    badge: 'Coming soon',
  },
  {
    id: 'guest',
    title: 'I’m a guest',
    desc: 'Follow events aboard.',
    Icon: BiSun,
  },
  {
    id: 'job',
    title: 'I’m looking for work or joining a crew',
    desc: INVITE_CODES_ENABLED
      ? 'Find a job aboard, or join a vessel you already work on.'
      : 'Build your crew profile so captains hiring your role can find you.',
    Icon: BiCompass,
  },
];

// Hints say only what happens today: there are no job alerts yet, and a vessel code joins
// straight away — the invitation is the captain's or owner's approval.
export const JOB_PATHS: Choice<JobPath>[] = [
  {
    id: 'job',
    title: 'I’m looking for a job',
    desc: 'Build your crew profile so captains hiring your role can find you.',
    Icon: FiCompass,
  },
  {
    id: 'team',
    title: 'I want to join my crew',
    desc: 'Enter the vessel code from your invitation and join your crew right away.',
    Icon: FiUserPlus,
  },
];

/** More than a few roles blurs every match a captain runs. */
export const MAX_JOB_ROLES = 3;

export type AvailableFrom = 'now' | '2w' | '1m' | '3m' | 'later';

/** When a job seeker can start — sent as the date each option starts on. */
export const AVAILABILITY_OPTIONS: { label: string; value: AvailableFrom }[] = [
  { label: 'Immediately', value: 'now' },
  { label: 'Within 2 weeks', value: '2w' },
  { label: 'Within a month', value: '1m' },
  { label: 'In 1–3 months', value: '3m' },
  { label: 'Later this year', value: 'later' },
];

export const CV_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.heic';

export const ROLES: Choice<Role>[] = [
  {
    id: 'owner',
    title: 'I’m the owner',
    desc: 'Enjoy your time aboard with family and guests while the crew runs the vessel.',
    Icon: FiBriefcase,
  },
  {
    id: 'captain',
    title: 'I’m the captain',
    desc: 'Run day-to-day operations, coordinate the crew and keep the owner in the loop.',
    Icon: FiAnchor,
  },
];

export const VESSEL_MAKES: SelectOption[] = [
  { label: 'Sunseeker', value: 'sunseeker' },
  { label: 'Ferretti', value: 'ferretti' },
  { label: 'Benetti', value: 'benetti' },
  { label: 'Azimut', value: 'azimut' },
  { label: 'Princess', value: 'princess' },
  { label: 'Lürssen', value: 'lurssen' },
  { label: 'Other', value: 'other' },
];

export const VESSEL_NEEDS: FeatureOption[] = [
  { id: 'crew', label: 'Crew management', Icon: FiUsers },
  { id: 'maint', label: 'Maintenance tracking', Icon: FiTool },
  { id: 'tasks', label: 'Task tracking', Icon: FiClipboard },
  { id: 'finance', label: 'Finance tracking', Icon: FiDollarSign },
  { id: 'events', label: 'Guest event planning', Icon: FiCalendar },
  { id: 'docs', label: 'Document storage', Icon: FiFileText },
];

export const CREW_ROLES_OPTS: FeatureOption[] = [
  { id: 'captain', label: 'Captain', Icon: FiAnchor },
  { id: 'stew', label: 'Steward', Icon: FiUsers },
  { id: 'chef', label: 'Chef', Icon: FiCoffee },
  { id: 'deckhand', label: 'Deckhand', Icon: FiCompass },
];

// marina renders the FaAnchor react-icon (matches the modal toggle), not an svg → icon ''.
export const SERVICE_TYPE_CHIPS: { id: string; label: string; icon: string }[] = [
  { id: 'marina', label: 'Marina Services', icon: '' },
  { id: 'hull', label: 'Hull & Exterior', icon: hullIcon },
  { id: 'wash', label: 'Exterior Wash & Detail', icon: washIcon },
  { id: 'interior', label: 'Interior & Cleaning', icon: interiorIcon },
  { id: 'mechanical', label: 'Mechanical & Electrical', icon: mechanicalIcon },
  { id: 'electronics', label: 'Electronics, Nav & Communication', icon: electronicsIcon },
  { id: 'safety', label: 'Safety, Inspection & Compliance', icon: safetyIcon },
  { id: 'concierge', label: 'Operational & Concierge', icon: conciergeIcon },
  { id: 'yachtmgmt', label: 'Yacht Management', icon: yachtmgmtIcon },
  { id: 'upholstery', label: 'Upholstery & Canvas', icon: upholsteryIcon },
  { id: 'crew', label: 'Crew Services', icon: crewIcon },
  { id: 'agent', label: 'Agent Services', icon: agentIcon },
  { id: 'emergency', label: 'Emergency Services', icon: emergencyIcon },
];

export const VALUE_LABEL = (opts: SelectOption[], v: string) =>
  opts.find((o) => o.value === v)?.label ?? '-';

export interface Form {
  vesselName: string;
  vesselMake: string;
  vesselModel: string;
  vesselLength: string;
  businessName: string;
  serviceDescription: string;
  contactEmail: string;
  contactPhone: string;
  contactWebsite: string;
  // marina sub-resource — mirrors the MarineInfo sub-form (all optional)
  marinaMaxLength: string;
  marinaMaxBeam: string;
  marinaDraft: string;
  marinaPriceDaily: string;
  marinaPriceMonthly: string;
  // job seeker — the crew profile step
  yearsExp: string;
  crewCount: string;
  vesselCode: string;
}

export const EMPTY_FORM: Form = {
  vesselName: '',
  vesselMake: '',
  vesselModel: '',
  vesselLength: '',
  businessName: '',
  serviceDescription: '',
  contactEmail: '',
  contactPhone: '',
  contactWebsite: '',
  marinaMaxLength: '',
  marinaMaxBeam: '',
  marinaDraft: '',
  marinaPriceDaily: '',
  marinaPriceMonthly: '',
  yearsExp: '',
  crewCount: '',
  vesselCode: '',
};

// Required form fields per step — add a step/field here to gate its Continue button.
// Steps backed by non-Form state (intent, role, goal, subscription) are gated in the component.
export const REQUIRED_FIELDS: Partial<Record<StepId, (keyof Form)[]>> = {
  vessel: ['vesselName'],
  serviceProfile: ['businessName', 'serviceDescription'],
  serviceContact: ['contactEmail', 'contactPhone'],
};

// Subscription tiers — Harbor (free), Navigate, Command. Prices are per seat / month.
export const PLANS: Plan[] = [
  { id: 'harbor', name: 'Harbor', Icon: FiAnchor, tagline: 'Get on the network.', free: true },
  {
    id: 'navigate',
    name: 'Navigate',
    Icon: FiNavigation,
    tagline: 'Run the boat, day to day.',
    monthly: 19.99,
  },
  {
    id: 'command',
    name: 'Command',
    Icon: FiAward,
    tagline: 'Own the operation.',
    monthly: 29.99,
  },
];

/** Marketing pricing page — the full feature matrix lives there, not in the wizard. */
export const PRICING_URL = 'https://www.vesselhaven.com/pricing';

export interface PlanGroup {
  id: string;
  label: string;
  features: string[];
}

export const PLAN_GROUPS: PlanGroup[] = [
  {
    id: 'community',
    label: 'Community & Network',
    features: [
      'Community ratings & feedback',
      'Browse & join communities',
      'Community discussions & calendar',
      'Discover events & activities',
      'Basic guest & location profiles',
    ],
  },
  {
    id: 'ops',
    label: 'Day-to-day Operations',
    features: [
      'Maintenance, equipment & tasks',
      'Events & smart calendar',
      'Team chat, notifications & SMS',
      'Documents & safety library',
      'Marina catalog + vendor payments',
      'Expense tracking & invoice AI',
    ],
  },
  {
    id: 'ownership',
    label: 'Ownership & Control',
    features: [
      'Budgets, approvals & crew payroll',
      'Crew hiring & management',
      'Full guest experience',
      'Digital logs (nav, safety, oil)',
      'Broadcast & priority support',
    ],
  },
];

// Which feature groups each tier unlocks (cumulative).
export const PLAN_INCLUDED: Record<PlanId, string[]> = {
  harbor: ['community'],
  navigate: ['community', 'ops'],
  command: ['community', 'ops', 'ownership'],
};

// All groups open by default so vessel owners can compare features across tiers.
export const INIT_EXPANDED: Record<string, boolean> = {
  'harbor:community': true,
  'harbor:ops': true,
  'harbor:ownership': true,
  'navigate:community': true,
  'navigate:ops': true,
  'navigate:ownership': true,
  'command:community': true,
  'command:ops': true,
  'command:ownership': true,
};

export const money = (n: number) => `$${n % 1 === 0 ? n : n.toFixed(2)}`;

// vessel, services & job flows are computed dynamically in the component (services splices
// in 'marinaInfo' when the Marina chip is picked); only guest is static.
export const FLOW: Record<'guest', FlowStep[]> = {
  guest: ['guestAllergies', 'guestDiet'],
};

export const STEP_TITLE: Record<FlowStep, string> = {
  role: 'Your role',
  vessel: 'About the vessel',
  needs: 'What you need',
  crewInfo: 'Your crew',
  serviceProfile: 'Your business',
  serviceTypeChips: 'Service types',
  marinaInfo: 'Marina details',
  serviceContact: 'Contact Info',
  location: 'Location',
  guestAllergies: 'Allergies',
  guestDiet: 'Dietary exclusions',
  vesselCode: 'Join a vessel',
  invite: 'Your captain',
  goal: 'Your goal',
  crewProfile: 'Crew profile',
  joinVessel: 'Join your vessel',
  review: 'All set',
  subscription: 'You’re all set',
};

export const STEP_HINT: Record<FlowStep, string> = {
  role: 'Owner or captain?',
  vessel: 'Name, make and length',
  needs: 'Pick your features',
  crewInfo: 'Who works aboard',
  serviceProfile: 'Tell owners what you offer',
  serviceTypeChips: 'Pick what you offer',
  marinaInfo: 'Berths and limits',
  serviceContact: 'How clients reach you',
  location: 'Where you work',
  guestAllergies: 'What the chef must know',
  guestDiet: 'What to leave off your plate',
  vesselCode: 'Your vessel code',
  invite: 'Invite them aboard',
  goal: 'Find a job or join your crew',
  crewProfile: CV_AUTOFILL_ENABLED ? 'Role, CV and availability' : 'Role and availability',
  joinVessel: 'Your vessel code',
  review: 'Check your answers',
  subscription: 'Your trial and next steps',
};

/** What the gifted Command trial unlocks, as the trial step lists it. */
export const TRIAL_INCLUDES = [
  'Budgets, approvals & crew payroll',
  'Crew hiring & management',
  'Guest planning, start to finish',
  'Digital logbooks: navigation, safety, oil',
  'Message everyone at once & priority alerts',
  'Browse & join communities',
  'Community discussions & calendar',
  'Discover events & activities',
  'Basic guest & location profiles',
];

export type InviteVia = 'email' | 'phone';

export const INVITE_VIA: readonly { label: string; value: InviteVia }[] = [
  { label: 'Email', value: 'email' },
  { label: 'Phone Number', value: 'phone' },
];

export interface InviteCopy {
  title: string;
  heading: string;
  lede: string;
  yes: { Icon: IconType; hint: string };
  no: { title: string; hint: string };
  card: string;
  field: string;
  placeholder: string;
  sent: string;
}

export const INVITE_COPY: Record<'captain' | 'owner', InviteCopy> = {
  captain: {
    title: 'Your captain',
    heading: 'Do you have a captain?',
    lede: 'Invite them and they’ll join your vessel as soon as they accept. You can also do this later.',
    yes: { Icon: BiAnchor, hint: 'Send your captain an invite by email or text message.' },
    no: { title: 'Not yet', hint: 'Skip for now. You can invite a captain any time from Crew.' },
    card: 'Invite your captain',
    field: 'Captain’s',
    placeholder: 'captain@example.com',
    sent: 'Once they accept, you’ll see them in Crew.',
  },
  owner: {
    title: 'Your owner',
    heading: 'Invite the owner',
    lede: 'They’ll follow what’s happening aboard and choose the plan for the vessel. You can also do this later.',
    yes: { Icon: BiBriefcase, hint: 'Send the owner an invite by email or text message.' },
    no: { title: 'Not Now', hint: 'You can invite the owner any time.' },
    card: 'Invite the owner',
    field: 'Owner’s',
    placeholder: 'owner@example.com',
    sent: 'Once they accept, they’ll join you aboard.',
  },
};
